// Site-wide ambient music player. One <audio> element lives in the
// MusicProvider mounted at the App shell, persists across page navigation
// (since React Router never reloads the document), and exposes simple
// methods for the rest of the app: enable / pause / resume / duck / unduck /
// pauseForSlideshow / resumeFromSlideshow.
//
// Browser autoplay rules require a user gesture before audio plays. We
// gate the whole thing on the interstitial Continue click, then store the
// "music enabled" preference in localStorage so subsequent navigations in
// the same session don't need another gesture.
//
// Voice ducking: while a Shao Kahn voice clip is playing we want the
// background music to drop to ~20% volume so the narration cuts through.
// The voice helper (lib/voice.ts) calls duckMusic() before play and
// unduckMusic() in the onended handler.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

// Hardcoded track list. To add more music, drop the file into
// src/client/public/slideshow/ and append to this list.
// Exported so other surfaces (Slideshow.tsx) can use the same rotation
// instead of being hardcoded to a single theme file.
// IMPORTANT: include EVERY MP3 in src/client/public/slideshow/ here so
// the same rotation drives both site-wide playback and slideshow mode.
// theme.mp3 was left out for a stretch when slideshow used it directly
// — that's been fixed; all four are now in the shared pool.
export const MUSIC_TRACKS: string[] = [
  "/slideshow/fuckin_up_at_7_eleven.mp3",
  "/slideshow/fuckin_up_at_walgreens.mp3",
  "/slideshow/still_fucking_up.mp3",
  "/slideshow/theme.mp3",
  "/slideshow/fuckin_tic_tacs.mp3",
  "/slideshow/fucked_up_rappin.mp3",
  "/slideshow/acoustic_fuckin_up.mp3",
];
const TRACKS = MUSIC_TRACKS;

const FULL_VOLUME = 0.55;
const DUCKED_VOLUME = 0.12;
const FADE_MS = 600;
const STORAGE_KEY = "musicEnabled";

/** Pretty-print a track URL into a human title (for the iOS lock-screen). */
function trackTitleFromUrl(url: string): string {
  const file = url.split("/").pop() || "Track";
  const stem = file.replace(/\.mp3$/i, "");
  return stem
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Push the current track's metadata into the platform Media Session so
 *  it shows on the iOS lock screen / Control Center AND the Android
 *  notification shade / lock screen. Also serves as the signal to the
 *  OS that we're a media app, which unlocks background-advance. */
function setMediaSessionForTrack(trackUrl: string): void {
  if (!("mediaSession" in navigator) || typeof MediaMetadata === "undefined") return;
  try {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: trackTitleFromUrl(trackUrl),
      artist: "u done fucked up.",
      album: "NJRC",
      // Album-art thumbnail. Android Chrome shows it prominently in the
      // notification + lock screen; iOS Safari uses it on the Control
      // Center "now playing" tile. Multiple sizes let the OS pick the
      // closest one to its display density.
      artwork: [
        { src: "/logo.png", sizes: "1254x1254", type: "image/png" },
        { src: "/logo.png", sizes: "512x512", type: "image/png" },
        { src: "/logo.png", sizes: "192x192", type: "image/png" },
      ],
    });
  } catch {
    /* MediaMetadata can throw on some browsers — safe to ignore */
  }
}

interface MusicContextValue {
  enabled: boolean;
  /** Live state of the underlying <audio> element — true when actually
   *  emitting sound, false when paused/ended/never started. Updates via
   *  audio event listeners installed in MusicProvider. */
  isPlaying: boolean;
  /** Mark audio as user-allowed and start the player (called by interstitial). */
  enable: () => void;
  /** Pause without forgetting the user wanted music. */
  pause: () => void;
  /** Resume after a pause. */
  resume: () => void;
  /** Drop volume for voiceover; auto-restores when ALL ducks released. */
  duck: () => void;
  unduck: () => void;
  /** Slideshow mode handoff. */
  pauseForSlideshow: () => void;
  resumeFromSlideshow: () => void;
  /** If enabled but audio isn't playing (paused/ended/errored), kick a fresh
   *  random track in. No-op if already playing or if not enabled. Safe to
   *  call freely from page-mount useEffects as a self-heal. */
  ensurePlaying: () => void;
}

const MusicContext = createContext<MusicContextValue | null>(null);

export function useMusic(): MusicContextValue {
  const v = useContext(MusicContext);
  if (!v) throw new Error("useMusic outside MusicProvider");
  return v;
}

export function MusicProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const duckCountRef = useRef(0);
  const slideshowPausedRef = useRef(false);
  const fadeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [enabled, setEnabled] = useState<boolean>(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const [isPlaying, setIsPlaying] = useState(false);

  // Lazy-create the audio element on first enable. Single element reused
  // for the lifetime of the SPA — survives React Router navigations.
  const ensureAudio = useCallback(() => {
    if (audioRef.current) return audioRef.current;
    const a = new Audio();
    a.loop = false;        // we manually advance to the next track
    a.volume = 0;          // start silent; fade in on play
    a.preload = "auto";
    a.crossOrigin = "anonymous";
    audioRef.current = a;
    a.addEventListener("ended", () => {
      // Pick a different random track than the one that just ended.
      pickAndPlayNext(a);
    });
    // Whenever a new track starts playing, prefetch the others so the
    // NEXT advance is instant (no gap between tracks while the browser
    // fetches MP3 bytes from the server). Combined with the `ended`
    // listener above this gives us seamless rotation.
    a.addEventListener("playing", () => {
      try { prefetchUpcomingTracks(a.src); } catch { /* non-fatal */ }
    });
    // If a track errors out mid-stream (network blip, codec hiccup,
    // bad file), don't just stop — pick a different random track and
    // keep going. Without this, a single 404 on a track silently
    // killed the whole rotation until the user navigated.
    a.addEventListener("error", () => {
      // eslint-disable-next-line no-console
      console.warn("[music] audio error — advancing to next track");
      pickAndPlayNext(a);
    });
    // Reflect actual playback state into React so the header toggle
    // shows the right icon. Listening to play/playing/pause/ended
    // covers all the transitions browsers fire.
    a.addEventListener("play", () => {
      setIsPlaying(true);
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
    });
    a.addEventListener("playing", () => {
      setIsPlaying(true);
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
    });
    a.addEventListener("pause", () => {
      setIsPlaying(false);
      if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
    });
    a.addEventListener("ended", () => setIsPlaying(false));
    // Wire up Media Session action handlers. CRITICAL for iOS Safari
    // background playback: when the screen is locked, iOS suspends the
    // ability to call audio.play() from arbitrary JS callbacks, including
    // the `ended` handler that advances to the next track. Without
    // MediaSession, the next-track sequence stutters because iOS thinks
    // we're trying to autoplay without permission. Registering as a
    // proper "media app" via these handlers tells iOS we ARE a player
    // and unlocks the background-advance permission.
    if ("mediaSession" in navigator) {
      try {
        navigator.mediaSession.setActionHandler("play", () => {
          a.play().catch(() => {});
        });
        navigator.mediaSession.setActionHandler("pause", () => {
          a.pause();
        });
        navigator.mediaSession.setActionHandler("nexttrack", () => {
          pickAndPlayNext(a);
        });
        navigator.mediaSession.setActionHandler("previoustrack", () => {
          // No history — just pick another random track.
          pickAndPlayNext(a);
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[music] mediaSession setup failed:", err);
      }
    }
    return a;
  }, []);

  const pickAndPlayNext = useCallback((a: HTMLAudioElement) => {
    if (TRACKS.length === 0) return;
    const cur = a.src;
    let next = TRACKS[Math.floor(Math.random() * TRACKS.length)]!;
    // Avoid same-track repeat unless there's only one
    if (TRACKS.length > 1) {
      let tries = 0;
      while (next && cur.endsWith(next) && tries < 4) {
        next = TRACKS[Math.floor(Math.random() * TRACKS.length)]!;
        tries++;
      }
    }
    a.src = next;
    // Update Media Session metadata so iOS lock-screen + Control Center
    // show the current track + treat us as a media app (which is what
    // unlocks background-advance permissions).
    setMediaSessionForTrack(next);
    a.play().catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("[music] play() blocked:", err);
    });
  }, []);

  // Pre-fetch one of the OTHER tracks (not the currently-playing one) so
  // when the audio element advances on `ended`, the browser already has
  // the bytes in cache and there's no audible gap. We use a fresh hidden
  // <link rel="prefetch"> per track so the browser handles dedup.
  // Called once at mount and re-called whenever the track changes.
  const prefetchUpcomingTracks = useCallback((excludeUrl: string) => {
    // ONLY prefetch ONE upcoming track. The previous version prefetched
    // ALL 6 others on every track-change, totalling ~50MB of parallel
    // HTTP streams from the browser. On the 2GB droplet that — combined
    // with stats, SSE, and page loads — was enough to OOM-kill the
    // node process. One next-track is plenty for "no gap when the
    // current song ends" — that's the only thing this prefetch needs
    // to enable.
    const candidates = TRACKS.filter((t) => !excludeUrl.endsWith(t));
    const next = candidates[Math.floor(Math.random() * candidates.length)];
    if (!next) return;
    // Avoid double-injecting prefetch links for the same file across
    // re-fires. The browser dedups but every link element accumulates
    // in <head> indefinitely otherwise.
    const existing = document.head.querySelector(`link[rel="prefetch"][href="${next}"]`);
    if (existing) return;
    const link = document.createElement("link");
    link.rel = "prefetch";
    link.as = "audio";
    link.href = next;
    document.head.appendChild(link);
  }, []);

  // Smooth volume tween. Cancels any in-flight fade.
  const fadeTo = useCallback((target: number, ms = FADE_MS) => {
    const a = audioRef.current;
    if (!a) return;
    if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
    const start = a.volume;
    const startedAt = Date.now();
    fadeTimerRef.current = setInterval(() => {
      const t = Math.min(1, (Date.now() - startedAt) / ms);
      a.volume = start + (target - start) * t;
      if (t >= 1) {
        clearInterval(fadeTimerRef.current!);
        fadeTimerRef.current = null;
      }
    }, 30);
  }, []);

  const targetVolume = useCallback(() => {
    if (slideshowPausedRef.current) return 0;
    return duckCountRef.current > 0 ? DUCKED_VOLUME : FULL_VOLUME;
  }, []);

  const enable = useCallback(() => {
    if (enabled) return;
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}
    setEnabled(true);
    const a = ensureAudio();
    pickAndPlayNext(a);
    fadeTo(targetVolume());
  }, [enabled, ensureAudio, pickAndPlayNext, fadeTo, targetVolume]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);
  const resume = useCallback(() => {
    const a = audioRef.current;
    if (!a) return;
    a.play().catch(() => {});
    fadeTo(targetVolume());
  }, [fadeTo, targetVolume]);

  const duck = useCallback(() => {
    duckCountRef.current++;
    if (audioRef.current) fadeTo(targetVolume(), 250);
  }, [fadeTo, targetVolume]);
  const unduck = useCallback(() => {
    duckCountRef.current = Math.max(0, duckCountRef.current - 1);
    if (audioRef.current) fadeTo(targetVolume(), 600);
  }, [fadeTo, targetVolume]);

  /**
   * Self-heal entrypoint. The audio element occasionally stops on its own —
   * track load error, browser background tab suspension, transient
   * network blip. This method, called from page-mount useEffects, checks
   * the player and kicks a fresh track if needed. Never restarts voice.
   */
  const ensurePlaying = useCallback(() => {
    if (!enabled) return;
    if (slideshowPausedRef.current) return; // slideshow owns the audio stage
    const a = audioRef.current ?? ensureAudio();
    // "Should be playing" but isn't → restart with a fresh random track.
    // We treat paused, ended, no-src, and errored as states needing a kick.
    const stalled = a.paused || a.ended || !a.src || a.error !== null;
    if (!stalled) return;
    pickAndPlayNext(a);
    fadeTo(targetVolume(), 800);
  }, [enabled, ensureAudio, pickAndPlayNext, fadeTo, targetVolume]);

  const pauseForSlideshow = useCallback(() => {
    slideshowPausedRef.current = true;
    fadeTo(0, 400);
    setTimeout(() => audioRef.current?.pause(), 450);
  }, [fadeTo]);
  const resumeFromSlideshow = useCallback(() => {
    slideshowPausedRef.current = false;
    if (!enabled || !audioRef.current) return;
    // Pick a fresh track so the user doesn't hear the same loop they had on
    // before slideshow.
    pickAndPlayNext(audioRef.current);
    fadeTo(targetVolume(), 800);
  }, [enabled, pickAndPlayNext, fadeTo, targetVolume]);

  // Cleanup on unmount (which only happens at SPA shutdown).
  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) clearInterval(fadeTimerRef.current);
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  // NO PERIODIC HEARTBEAT. An earlier version called ensurePlaying every
  // 5s as a "self-heal" but that broke the contract that PAUSE means
  // PAUSE — if the user hit pause, or started music in another app, the
  // heartbeat would barge back in 5 seconds later and reassert itself.
  // The `ended` event listener (above) handles legitimate track-end
  // advances; `error` handles broken-track recovery. Music starts only
  // on explicit triggers: enable() on first user gesture, ensurePlaying()
  // explicitly called from page-mount useEffects (Lookup page), or
  // resumeFromSlideshow(). User pause is final until they un-pause.

  const value = useMemo<MusicContextValue>(
    () => ({ enabled, isPlaying, enable, pause, resume, duck, unduck, pauseForSlideshow, resumeFromSlideshow, ensurePlaying }),
    [enabled, isPlaying, enable, pause, resume, duck, unduck, pauseForSlideshow, resumeFromSlideshow, ensurePlaying],
  );

  return <MusicContext.Provider value={value}>{children}</MusicContext.Provider>;
}
